import { useEffect, useRef, useState } from 'react';
import Icon from './Icon';

// Combobox buscable simple, sin dependencias externas. Muestra `value` tal
// cual cuando está cerrado (incluso si no pertenece a `options`, para no
// romper valores históricos), y solo permite fijar un nuevo valor mediante
// selección de una opción de la lista — nunca texto libre.
function SearchableSelect({
  id,
  label,
  value,
  onChange,
  options,
  placeholder = 'Seleccionar',
  error,
  status = 'ready',
  loadingLabel = 'Cargando productos...',
  errorLabel = 'Error al cargar productos.',
  emptyLabel = 'Sin productos disponibles.',
}) {
  const disabled = status !== 'ready';
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const containerRef = useRef(null);

  const filtered =
    query.trim() === ''
      ? options
      : options.filter((option) => option.toLowerCase().includes(query.trim().toLowerCase()));

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openList = () => {
    setOpen(true);
    setQuery('');
    setHighlighted(0);
  };

  const closeList = () => {
    setOpen(false);
    setQuery('');
  };

  const selectOption = (option) => {
    onChange(option);
    closeList();
  };

  const handleKeyDown = (event) => {
    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'Enter') {
        event.preventDefault();
        openList();
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (filtered[highlighted]) selectOption(filtered[highlighted]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      closeList();
    }
  };

  const displayValue = open && !disabled ? query : value || '';
  const statusPlaceholder =
    status === 'loading' ? loadingLabel : status === 'error' ? errorLabel : status === 'empty' ? emptyLabel : placeholder;
  const helperText = status === 'error' ? errorLabel : error;

  return (
    <div className="w-full" ref={containerRef}>
      {label && (
        <label htmlFor={id} className="mb-1 block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          id={id}
          type="text"
          role="combobox"
          aria-expanded={open && !disabled}
          aria-controls={`${id}-listbox`}
          aria-autocomplete="list"
          aria-busy={status === 'loading'}
          autoComplete="off"
          disabled={disabled}
          value={displayValue}
          placeholder={statusPlaceholder}
          onFocus={disabled ? undefined : openList}
          onClick={disabled ? undefined : openList}
          onChange={
            disabled
              ? undefined
              : (event) => {
                  if (!open) setOpen(true);
                  setQuery(event.target.value);
                  setHighlighted(0);
                }
          }
          onKeyDown={disabled ? undefined : handleKeyDown}
          onBlur={disabled ? undefined : closeList}
          className={`min-h-[48px] w-full rounded-xl border px-4 pr-10 text-base placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-200 ${
            disabled ? 'cursor-not-allowed bg-gray-50 text-gray-400' : 'text-gray-900'
          } ${error || status === 'error' ? 'border-red-400' : 'border-gray-300 focus:border-green-600'}`}
        />
        <Icon
          name="chevronDown"
          className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
        />

        {open && !disabled && (
          <ul
            id={`${id}-listbox`}
            role="listbox"
            className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
          >
            {filtered.length === 0 && <li className="px-4 py-3 text-sm text-gray-500">Sin resultados</li>}
            {filtered.map((option, index) => (
              <li
                key={option}
                role="option"
                aria-selected={option === value}
                onMouseDown={(event) => {
                  event.preventDefault();
                  selectOption(option);
                }}
                className={`cursor-pointer px-4 py-3 text-base ${
                  index === highlighted ? 'bg-green-50 text-green-800' : 'text-gray-900'
                } ${option === value ? 'font-medium' : ''}`}
              >
                {option}
              </li>
            ))}
          </ul>
        )}
      </div>
      {helperText && <p className="mt-1 text-sm text-red-600">{helperText}</p>}
    </div>
  );
}

export default SearchableSelect;
